import { Button, Group, Modal, Stack, Text, Tooltip } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";

import { NoticeDisplayType } from "src/api/Tmd.dtos";
import Icon from "src/components/common/Icon";
import RichTextContent from "src/components/common/RichTextContent";
import useNoticesContext from "src/hooks/notices/useNoticesContext";

import { getNoticeSeverityPresentation } from "./noticeSeverityPresentation";

/**
 * Displays the first eligible modal notice, retaining locally closed IDs while they remain available.
 * Postconditions: Closing requests local hiding on a later render without server acknowledgement.
 * The effect forgets closed IDs absent from the full context list, allowing later reappearance.
 * Assumptions: Context order chooses the next notice; acknowledgement owns persistence and errors.
 */
const NoticeModals = () => {
    const { notices, acknowledge } = useNoticesContext();
    const [closedIds, setClosedIds] = useState<ReadonlySet<number>>(new Set());
    const modalNotices = useMemo(
        () =>
            notices.filter(notice =>
                notice.displayOption === NoticeDisplayType.Modal &&
                !closedIds.has(notice.id)
            ),
        [closedIds, notices]
    );

    const current = modalNotices[0];
    const severity = getNoticeSeverityPresentation(current?.severity);
    const severityBorder = `var(--mantine-color-${severity.color}-6)`;
    const severityBackground = `var(--mantine-color-${severity.color}-light)`;
    const severityBodyBackground = `color-mix(in srgb, ${severityBackground} 55%, var(--mantine-color-body))`;
    const severityText = `var(--mantine-color-${severity.color}-light-color)`;

    // Keep closed IDs only while they still occur in the context, including non-modal notices.
    useEffect(
        () => {
            const availableIds = new Set(notices.map(notice => notice.id));
            setClosedIds(ids => {
                const retained = new Set([...ids].filter(id => availableIds.has(id)));
                return retained.size === ids.size ? ids : retained;
            }
            );
        },
        [notices]
    );

    /**
     * Requests local closure of the currently displayed notice.
     * Postconditions: With a current notice, its ID is added to a new set for a later render.
     * With no current notice, no update is requested; closing never acknowledges on the server.
     */
    const close = () => {
        if (current) {
            setClosedIds(ids => new Set(ids).add(current.id));
        }
    };

    return (
        <Modal
            opened={!!current}
            onClose={close}
            title={
                current ? (
                    <Group
                        gap="xs"
                        wrap="nowrap"
                    >
                        <Icon
                            icon={severity.icon}
                            aria-hidden
                        />
                        <Text fw={600}>{current.title}</Text>
                    </Group>
                ) : undefined
            }
            centered
            size="lg"
            data-notice-modal
            data-notice-severity={severity.label.toLowerCase()}
            styles={{
                content: {
                    border: `1px solid ${severityBorder}`,
                    backgroundColor: severityBodyBackground
                },
                header: {
                    backgroundColor: severityBackground,
                    borderBottom: `1px solid ${severityBorder}`,
                    color: severityText
                },
                title: { color: severityText },
                close: { color: severityText },
                body: {
                    backgroundColor: severityBodyBackground,
                    paddingTop: "var(--mb-padding, var(--mantine-spacing-md))"
                }
            }}
        >
            {current && (
                <Stack>
                    <RichTextContent
                        content={current.message}
                        fz="sm"
                    />
                    <Group justify="flex-end">
                        <Button
                            variant="subtle"
                            color={severity.color}
                            onClick={close}
                        >
                            Close
                        </Button>
                        {current.dismissible && (
                            <Tooltip label="Acknowledge this notice to dismiss it and prevent it from appearing again.">
                                <Button
                                    variant="filled"
                                    color={severity.color}
                                    onClick={() => void acknowledge(current.id)}
                                >
                                    Acknowledge
                                </Button>
                            </Tooltip>
                        )}
                    </Group>
                </Stack>
            )}
        </Modal>
    );
};

export default NoticeModals;
